import * as THREE from 'three';
import { RoundedBoxGeometry } from 'three/addons/geometries/RoundedBoxGeometry.js';

import {
  type LabSceneFactory,
  createBeaker,
  createHotPlate,
  createLabStage,
  createRippleSurface,
  labGlassMaterial,
  refractiveLiquidMaterial,
  seeded,
  softPuffTexture,
} from './labScene3d';
import type { HeatingPhase, StateFractions } from './particleModel';

/** What the 3D beaker shows. Every value comes from the deterministic heating model. */
export interface WaterBeakerState {
  phase: HeatingPhase;
  fractions: StateFractions;
  temperatureC: number;
  /** Sample size as a share of the beaker (0–1); scales the water level and the number of ice cubes. */
  fill?: number;
}

// World units; the scene is a teaching view of one sample of water, not to scale.
const FLOOR_Y = -1.26;
const H = 3.2;
const FULL_LIQUID = 1.95;
const ICE_CUBES = 8;
const ICE_SIZE = 0.6;
const BUBBLES = 70;
const STEAM_SPRITES = 64;
const THERMO = { x: 0.92, z: 0.25, tilt: -0.2, length: 3.9, bottomY: FLOOR_Y + 0.28 } as const;
const T_MIN = -20;
const T_MAX = 120;

export const createBeakerScene: LabSceneFactory<WaterBeakerState> = (renderer, palette, reducedMotion) => {
  const stage = createLabStage(renderer, palette, reducedMotion);
  const { scene, keep } = stage;
  const plate = createHotPlate(stage, FLOOR_Y);
  const beaker = createBeaker(stage, { floorY: FLOOR_Y, height: H });
  const INNER_R = beaker.innerRadius;
  const INNER_FLOOR = beaker.innerFloorY;

  // Liquid water: refractive, with depth-based blue colour.
  const waterMaterial = refractiveLiquidMaterial(stage, palette.liquid);
  const waterBody = new THREE.Mesh(keep(new THREE.CylinderGeometry(INNER_R, INNER_R, 1, 96, 1, true)), waterMaterial);
  const waterFloor = new THREE.Mesh(keep(new THREE.CircleGeometry(INNER_R, 96)), waterMaterial);
  waterFloor.rotation.x = -Math.PI / 2;
  waterFloor.position.y = INNER_FLOOR + 0.002;
  scene.add(waterBody, waterFloor);
  const surface = createRippleSurface(stage, INNER_R, waterMaterial);

  // Ice: rounded, clear cubes with a faint blue body colour.
  const iceMaterial = keep(new THREE.MeshPhysicalMaterial({
    color: palette.liquid.clone().lerp(new THREE.Color(0xffffff), 0.75),
    roughness: 0.08,
    transmission: 1,
    thickness: 1,
    ior: 1.31,
    attenuationColor: palette.liquid,
    attenuationDistance: 1.4,
    clearcoat: 1,
    clearcoatRoughness: 0.1,
    envMapIntensity: 1.4,
  }));
  const ice = new THREE.InstancedMesh(keep(new RoundedBoxGeometry(ICE_SIZE, ICE_SIZE, ICE_SIZE, 4, 0.11)), iceMaterial, ICE_CUBES);
  ice.castShadow = true;
  scene.add(ice);
  const iceSpots = Array.from({ length: ICE_CUBES }, (_, index) => ({
    stackX: -0.3 + (index % 2 === 0 ? -0.33 : 0.33) + (seeded(index, 1) - 0.5) * 0.06,
    stackZ: (Math.floor(index / 2) % 2 === 0 ? -0.33 : 0.33) + (seeded(index, 2) - 0.5) * 0.06,
    layer: Math.floor(index / 4),
    floatX: -0.2 + Math.cos((index / ICE_CUBES) * Math.PI * 2) * (index === 0 ? 0 : 0.58),
    floatZ: Math.sin((index / ICE_CUBES) * Math.PI * 2) * (index === 0 ? 0 : 0.58),
    spin: seeded(index, 5) * Math.PI,
  }));

  // Boiling bubbles. Not transmissive: refractive water cannot show other refractive objects.
  const bubbles = new THREE.InstancedMesh(
    keep(new THREE.SphereGeometry(0.06, 14, 10)),
    keep(new THREE.MeshPhysicalMaterial({ color: 0xffffff, roughness: 0.05, transparent: true, opacity: 0.7, clearcoat: 1, envMapIntensity: 2.5 })),
    BUBBLES,
  );
  scene.add(bubbles);

  // Thermometer: glass tube, red bulb and a red column that follows the model temperature.
  const thermo = new THREE.Group();
  thermo.position.set(THERMO.x, THERMO.bottomY, THERMO.z);
  thermo.rotation.z = THERMO.tilt;
  const tubeMaterial = labGlassMaterial(stage, 0.35);
  tubeMaterial.side = THREE.DoubleSide;
  const tube = new THREE.Mesh(keep(new THREE.CylinderGeometry(0.1, 0.1, THERMO.length, 24)), tubeMaterial);
  tube.position.y = THERMO.length / 2;
  tube.renderOrder = 12;
  const mercuryMaterial = keep(new THREE.MeshPhysicalMaterial({ color: palette.accent, roughness: 0.2, clearcoat: 1, emissive: palette.accent, emissiveIntensity: 0.35 }));
  const bulb = new THREE.Mesh(keep(new THREE.SphereGeometry(0.15, 20, 14)), mercuryMaterial);
  const column = new THREE.Mesh(keep(new THREE.CylinderGeometry(0.05, 0.05, 1, 12)), mercuryMaterial);
  thermo.add(tube, bulb, column);
  scene.add(thermo);

  // Steam: soft sprites that rise, grow and fade.
  const puff = softPuffTexture(stage);
  const steam = Array.from({ length: STEAM_SPRITES }, () => {
    const material = keep(new THREE.SpriteMaterial({ map: puff, color: palette.steam, transparent: true, depthWrite: false, opacity: 0 }));
    const sprite = new THREE.Sprite(material);
    sprite.renderOrder = 20;
    scene.add(sprite);
    return { sprite, material };
  });

  stage.finish();

  const matrix = new THREE.Matrix4();
  const position = new THREE.Vector3();
  const quaternion = new THREE.Quaternion();
  const euler = new THREE.Euler();
  const scale = new THREE.Vector3();
  const hide = () => matrix.makeScale(0, 0, 0);

  return {
    update: (state, t, playing) => {
      const { phase, fractions, temperatureC } = state;
      const fill = THREE.MathUtils.clamp(state.fill ?? 1, 0.04, 1);
      const heat = THREE.MathUtils.clamp((temperatureC - T_MIN) / (T_MAX - T_MIN), 0, 1);
      plate.setHeat(heat);

      // Liquid column and rippling surface.
      const liquidHeight = fractions.liquid * FULL_LIQUID * fill;
      const hasLiquid = liquidHeight > 0.015;
      const waterTop = INNER_FLOOR + liquidHeight;
      waterBody.visible = hasLiquid;
      waterFloor.visible = hasLiquid;
      surface.mesh.visible = hasLiquid;
      waterBody.scale.y = Math.max(liquidHeight, 0.001);
      waterBody.position.y = INNER_FLOOR + liquidHeight / 2;
      surface.mesh.position.y = waterTop;
      const boiling = phase === 'boiling';
      surface.update(boiling ? 0.055 : temperatureC > 70 ? 0.014 : 0.006, t, boiling);

      // Ice: stacked on the floor when there is no liquid; floating and bobbing on liquid water.
      const iceAmount = fractions.solid > 0 ? Math.max(fractions.solid * ICE_CUBES * fill, 0.6) : 0;
      iceSpots.forEach((spot, index) => {
        const size = THREE.MathUtils.clamp(iceAmount - index, 0, 1);
        if (size <= 0.03) {
          ice.setMatrixAt(index, hide());
          return;
        }
        const cubeScale = 0.45 + 0.55 * Math.cbrt(size);
        if (hasLiquid) {
          // Real ice floats about 90 % under water; the view lifts it so the floating cubes stay readable.
          position.set(spot.floatX, waterTop + ICE_SIZE * cubeScale * 0.12 + Math.sin(t * 1.4 + index) * 0.03, spot.floatZ);
          euler.set(Math.sin(t * 0.9 + index) * 0.08, spot.spin + t * 0.06, Math.cos(t * 0.8 + index) * 0.07);
        } else {
          position.set(spot.stackX, INNER_FLOOR + (ICE_SIZE / 2) * cubeScale + spot.layer * ICE_SIZE * 1.01, spot.stackZ);
          euler.set(0, spot.spin * 0.25, 0);
        }
        ice.setMatrixAt(index, matrix.compose(position, quaternion.setFromEuler(euler), scale.setScalar(cubeScale)));
      });
      ice.instanceMatrix.needsUpdate = true;

      // Bubbles: a few nucleation bubbles above 90 °C, a full rolling boil at 100 °C.
      const activeBubbles = !hasLiquid ? 0 : boiling ? BUBBLES : temperatureC > 90 ? Math.round(((temperatureC - 90) / 10) * 14) : 0;
      for (let index = 0; index < BUBBLES; index += 1) {
        if (index >= activeBubbles) {
          bubbles.setMatrixAt(index, hide());
          continue;
        }
        const progress = (t * (0.5 + seeded(index, 6) * 0.6) + seeded(index, 7)) % 1;
        const angle = seeded(index, 8) * Math.PI * 2;
        const radius = Math.sqrt(seeded(index, 9)) * (INNER_R - 0.12);
        position.set(
          Math.cos(angle) * radius + Math.sin(t * 4 + index) * 0.025,
          INNER_FLOOR + 0.06 + progress * Math.max(liquidHeight - 0.08, 0),
          Math.sin(angle) * radius,
        );
        bubbles.setMatrixAt(index, matrix.compose(position, quaternion.identity(), scale.setScalar(0.5 + progress * 1.6)));
      }
      bubbles.instanceMatrix.needsUpdate = true;

      // Thermometer column.
      const columnLength = 0.15 + heat * (THERMO.length - 0.5);
      column.scale.y = columnLength;
      column.position.y = columnLength / 2;

      // Steam: faint above hot water, strong while boiling, filling the beaker as all water turns to steam.
      const steamShare = phase === 'gas' ? 1 : boiling ? 0.55 + fractions.gas * 0.45 : temperatureC > 60 ? ((temperatureC - 60) / 40) * 0.3 : 0;
      const activeSteam = Math.round(steamShare * STEAM_SPRITES);
      steam.forEach(({ sprite, material }, index) => {
        if (index >= activeSteam) {
          sprite.visible = false;
          return;
        }
        sprite.visible = true;
        const progress = (t * (0.07 + seeded(index, 10) * 0.07) + seeded(index, 11)) % 1;
        const angle = seeded(index, 12) * Math.PI * 2 + t * 0.15;
        const startY = phase === 'gas' ? INNER_FLOOR + 0.2 : hasLiquid ? waterTop : beaker.rimY - 0.6;
        const rise = phase === 'gas' ? H + 2.4 : beaker.rimY + 2.6 - startY;
        const spread = phase === 'gas' ? 0.25 + progress * 1.6 : 0.15 + progress * progress * 1.8;
        sprite.position.set(Math.cos(angle) * spread * (0.4 + seeded(index, 13) * 0.6), startY + progress * rise, Math.sin(angle) * spread * (0.4 + seeded(index, 14) * 0.6));
        const size = 0.55 + progress * 1.7;
        sprite.scale.set(size, size, 1);
        material.opacity = Math.sin(Math.PI * progress) * (phase === 'gas' ? 0.5 : 0.42) * Math.min(1, steamShare * 2);
      });

      stage.render(playing);
    },
    resize: stage.resize,
    dispose: stage.dispose,
  };
};
